import { useTranslation } from 'react-i18next';
import {
  useEffect, useMemo, useState,
  FocusEvent,
} from 'react';
import { Select } from 'antd';
import _ from 'lodash';
import { StyledSelectWrapper } from 'components/styledComponents/select/Select.styled';
import type { DefaultOptionType } from 'antd/es/select';
import { AdminMultiProps, SelectOptionProps, IdType } from './AdminSelect';

const { Option } = Select;

const AdminMultiSelect = <
  OptionType extends SelectOptionProps,
  IsToString extends boolean = true
>({
    id = '', data, isToString = true as IsToString, onChange: onChangeProps, onBlur: onBlurProps, value = [], isSelectAll = true, searchType = 'startsWith', renderOptions, onOpenChange, ...rest
  }:AdminMultiProps<OptionType, IsToString>) => {
  const { t } = useTranslation();

  type Data = IdType<OptionType, IsToString>

  const [filterState, setFilterState] = useState<Data[]>([]);
  const [options, setOptions] = useState<OptionType[]|undefined>(data);
  const all = useMemo<Data[]>(
    () => data?.map((el) => (isToString ? el.id.toString() : el.id) as Data) || [],
    [data, isToString],
  );

  useEffect(() => {
    setOptions(data);
  }, [data]);

  const unique = isSelectAll && filterState.length ? _.union(value, filterState) : all;
  const intersect = isSelectAll && filterState.length ? _.intersection(value, filterState) : all;

  const getSelectedIds = (idValues: (Data | 'all')[]): Data[] => {
    if (isSelectAll && idValues?.includes('all')) {
      if (idValues.length === all.length + 1 && intersect.length === all.length) {
        return [];
      }

      if (!_.isEmpty(filterState)) {
        if (value?.length && value.length === unique.length) {
          return _.difference(value, filterState);
        }
        return unique;
      }

      return all;
    }
    return idValues;
  };

  const matchSearch = (input: string, name:string = '') => name.toString().toLowerCase()[searchType](input.toLowerCase());

  const onChange = (values: (Data | 'all')[]) => {
    onChangeProps?.(getSelectedIds(values));
    setFilterState([]);
  };

  const onSearch = isSelectAll
    ? (input: string) => {
      if (data) {
        const filtered: Data[] = data.reduce((acc, curr) => {
          if (input && matchSearch(input, curr.name)) {
            acc.push(isToString ? curr.id.toString() : curr.id as Data);
          }
          return acc;
        }, [] as Data[]);

        setFilterState(filtered);
      }
    }
    : undefined;

  const filterOption = (input: string, option?: DefaultOptionType) => {
    if (option?.value === 'all') {
      return !!data?.find((el) => matchSearch(input, el.name));
    }
    return matchSearch(input, String(option?.children ?? ''));
  };

  const onBlur = (e:FocusEvent<HTMLElement>) => {
    onBlurProps?.(e);
    setFilterState([]);
  };

  const moveSelectedOptionsToTop = () => {
    // Form.Item name prop value === id
    const selectedIndexMap = new Map();
    if (value) {
      (id ? value : value.map(String)).forEach((val, index) => {
        selectedIndexMap.set(val.toString(), index);
      });
    }

    return setOptions(
      selectedIndexMap.size
        ? data?.toSorted((a, b) => {
          const aID = a.id.toString();
          const bID = b.id.toString();
          const aSelected = selectedIndexMap.has(aID);
          const bSelected = selectedIndexMap.has(bID);
          if (aSelected && !bSelected) {
            return -1;
          }
          if (!aSelected && bSelected) {
            return 1;
          }
          if (aSelected && bSelected) {
            const aIndex = selectedIndexMap.get(aID);
            const bIndex = selectedIndexMap.get(bID);
            return aIndex - bIndex;
          }
          return 0;
        })
        : data,
    );
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    if (open) {
      moveSelectedOptionsToTop();
    }

    onOpenChange?.(open);
  };

  return (
    <StyledSelectWrapper
      mode="multiple"
      showSearch={{
        filterOption,
        onSearch,
      }}
      onBlur={onBlur}
      onChange={onChange}
      onOpenChange={handleDropdownVisibleChange}
      value={value}
      classNames={{
        popup: {
          root: 'multi-select',
        },
      }}
      {...rest}
    >
      {data?.length && isSelectAll && (
        <Option
          key="all"
          value="all"
          className={`${value?.length && unique.length === value?.length ? 'ant-select-item-option-selected' : ''}
                  ${value?.length && value?.length < unique.length && intersect.length ? 'ant-select-option-indeterminate' : ''}`}
        >
          <span>{t('selectAll')}</span>
        </Option>
      )}
      {options && renderOptions?.(options)}
    </StyledSelectWrapper>
  );
};

export default AdminMultiSelect;
